const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm', '.m4v'];

function isVideoKey(key: string): boolean {
  const lower = key.toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * A content-history row's thumbnail. `mediaKey` names the storage key found
 * in the row's output (or null — a text/no-output node); `url` is the
 * already-resolved signed URL for that key (undefined = still signing,
 * null = not signable/forbidden) — resolved once per PAGE by the caller
 * (UserContentTab), not per thumbnail, to keep this one media-sign call per
 * page load rather than one per row.
 *
 * A video key NEVER reaches an `<img>` (it would render as a broken image
 * icon — browsers do not decode video containers as images): a `<video
 * preload="metadata">` shows the first frame as a poster without fetching
 * the whole file.
 */
export function ContentMediaThumb({
  mediaKey,
  url,
  kind,
}: {
  mediaKey: string | null;
  url: string | null | undefined;
  /** Overrides the extension guess, for keys that carry no extension. */
  kind?: 'video' | 'image' | 'audio' | 'file';
}) {
  const boxClass =
    'flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-card border border-border bg-surface-muted';

  if (!mediaKey) {
    return (
      <div className={boxClass}>
        <span className="text-micro text-fg-faint">—</span>
      </div>
    );
  }
  if (url === undefined) {
    return <div className={`${boxClass} animate-pulse`} aria-label="Loading preview" />;
  }
  if (url === null) {
    return (
      <div className={boxClass} title="Preview unavailable">
        <span className="text-micro text-fg-faint">n/a</span>
      </div>
    );
  }
  if (kind === 'audio' || kind === 'file') {
    return (
      <div className={boxClass} title={mediaKey}>
        <span className="text-micro text-fg-hint">{kind}</span>
      </div>
    );
  }
  if (kind === 'video' || (kind === undefined && isVideoKey(mediaKey))) {
    return (
      <video
        className={boxClass}
        preload="metadata"
        muted
        aria-label="Video preview"
        data-testid="content-video-thumb"
      >
        <source src={`${url}#t=0.1`} />
      </video>
    );
  }
  return (
    <div className={boxClass}>
      <img
        src={url}
        alt="Content preview"
        className="h-full w-full object-cover"
        data-testid="content-image-thumb"
      />
    </div>
  );
}
